import {
  Sparkles, Bot, Map, Mail, UserSearch, HardDrive, Layers,
  MapPin, Waves, Mountain, KeyRound, Wrench, Database, ExternalLink, Lock,
} from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";
import { useAuth } from "../context/AuthContext";

const STATUS_TONE = {
  Active: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  "Configured": "bg-sky-500/15 text-sky-500 border-sky-500/30",
  "Action needed": "bg-amber-500/15 text-amber-500 border-amber-500/30",
  Public: "bg-teal-500/15 text-teal-500 border-teal-500/30",
};

const INTEGRATIONS = [
  {
    slug: "anthropic", name: "Anthropic Claude", provider: "Anthropic", icon: Sparkles,
    category: "AI · Text Generation", status: "Active",
    purpose: "Primary large-language model powering Angela's investor briefs, deal reads, and written analysis.",
    auth: "Requires the customer's own Anthropic API key (server-side).",
    whereUsed: "backend/ai.py (Angela prompts & brief generation).",
    dataType: "Prompt text in, generated narrative out. No property data is retained by the model.",
    docs: "https://docs.anthropic.com/",
  },
  {
    slug: "openai", name: "OpenAI", provider: "OpenAI", icon: Bot,
    category: "AI · Fallback Text / Vision", status: "Active",
    purpose: "Fallback text generation and vision tasks when the primary model is unavailable.",
    auth: "Requires the customer's own OpenAI API key (server-side).",
    whereUsed: "backend/ai.py (fallback path for Angela).",
    dataType: "Prompt/image in, generated text out.",
    docs: "https://platform.openai.com/docs",
  },
  {
    slug: "mapbox", name: "Mapbox GL", provider: "Mapbox", icon: Map,
    category: "Mapping · Basemaps", status: "Active",
    purpose: "Interactive maps for the Land Recon explorer — basemaps, satellite, parcel & zoning overlays.",
    auth: "Requires a Mapbox public access token (frontend).",
    whereUsed: "frontend/src/pages/PropertyIntelligence.jsx (Mapbox GL map).",
    dataType: "Map tiles and vector layers rendered client-side.",
    docs: "https://docs.mapbox.com/",
  },
  {
    slug: "postmark", name: "Postmark", provider: "Postmark (ActiveCampaign)", icon: Mail,
    category: "Email · Transactional", status: "Configured",
    purpose: "Transactional email delivery (investor brief links, notifications).",
    auth: "Requires a Postmark server API token (server-side).",
    whereUsed: "backend email provider (transactional sends & webhooks).",
    dataType: "Recipient, subject, and message body.",
    docs: "https://postmarkapp.com/developer",
  },
  {
    slug: "postgrid", name: "PostGrid Print & Mail", provider: "PostGrid", icon: Mail,
    category: "Direct Mail · Letters & Postcards", status: "Action needed",
    purpose: "Sends Arqitek-branded physical mailers (letters & postcards) to property owners from the Lead record — return address 30141 Antelope Rd. #613, Menifee, CA 92584.",
    auth: "Requires PostGrid API keys (test + live). Add POSTGRID_TEST_API_KEY / POSTGRID_LIVE_API_KEY to backend/.env — currently blank until keys are pasted.",
    whereUsed: "backend/postgrid.py → POST /postgrid/lead/{id}/send; Lead record Actions → 'Send Mailer'.",
    dataType: "Owner name + mailing/property address in; order id, status & PDF preview out. Test mode never prints/charges; live mode prints & mails (billable).",
    docs: "https://guides.postgrid.com/print-and-mail/api/overview/",
  },
  {
    slug: "batchdata", name: "BatchData Skip Trace", provider: "BatchData", icon: UserSearch,
    category: "Data · Owner Skip Trace", status: "Action needed",
    purpose: "Address-first owner skip trace — returns owner-of-record, mailing address, phone(s) and email(s) for outreach.",
    auth: "Requires a BatchData API token WITH the Property Skip Trace product enabled. Token is present but currently returns 403 until the Skip Trace permission is added to the account.",
    whereUsed: "backend/property_intelligence.py → POST /pi/records/{id}/skip-trace (manual 'Owner lookup' button).",
    dataType: "Property address in; owner + contact details out. Paid per matched lookup (results cached per address).",
    docs: "https://developer.batchdata.com/docs/batchdata/welcome-to-batchdata",
  },
  {
    slug: "object-storage", name: "Emergent Object Storage", provider: "Emergent", icon: HardDrive,
    category: "Storage · Files & Documents", status: "Active",
    purpose: "Persistent cloud storage for uploaded documents, images and generated files.",
    auth: "Uses the Emergent platform key (managed).",
    whereUsed: "backend/documents.py (upload / retrieve).",
    dataType: "Binary files stored and served via signed URLs.",
    docs: null,
  },
  {
    slug: "arcgis", name: "ArcGIS REST Services", provider: "Esri / Municipal GIS hosts", icon: Layers,
    category: "GIS · Parcels & Zoning", status: "Public",
    purpose: "Municipal/county parcel boundaries (APN, owner, acreage) and zoning overlays for the map (KC Metro, Rockwall TX, and more).",
    auth: "No key required — public FeatureServer / MapServer endpoints.",
    whereUsed: "backend/property_intelligence.py (PARCEL_LAYERS, ZONING_LAYERS).",
    dataType: "GeoJSON parcel & zoning features by viewport/point.",
    docs: "https://developers.arcgis.com/rest/",
  },
  {
    slug: "census", name: "US Census Bureau Geocoder", provider: "US Census Bureau", icon: MapPin,
    category: "Geocoding · Identity", status: "Public",
    purpose: "Address ↔ coordinate geocoding plus city, county, state and ZIP (via ZCTA) for the Property Details panel.",
    auth: "No key required — public federal API.",
    whereUsed: "backend/property_intelligence.py (CensusGeocoderAdapter).",
    dataType: "Address/coordinates in; matched address, geographies & ZIP out.",
    docs: "https://geocoding.geo.census.gov/geocoder/",
  },
  {
    slug: "fema", name: "FEMA National Flood Hazard Layer", provider: "FEMA", icon: Waves,
    category: "Environmental · Flood", status: "Public",
    purpose: "Flood zone determination at the parcel location.",
    auth: "No key required — public federal service.",
    whereUsed: "backend/property_intelligence.py (FemaFloodAdapter).",
    dataType: "Point in; flood zone designation out.",
    docs: "https://www.fema.gov/flood-maps/national-flood-hazard-layer",
  },
  {
    slug: "usgs", name: "USGS Elevation Point Query Service", provider: "USGS", icon: Mountain,
    category: "Environmental · Elevation", status: "Public",
    purpose: "Ground elevation at the parcel location.",
    auth: "No key required — public federal service.",
    whereUsed: "backend/property_intelligence.py (UsgsElevationAdapter).",
    dataType: "Point in; elevation (ft) out.",
    docs: "https://apps.nationalmap.gov/epqs/",
  },
];

function DetailRow({ icon: Icon, label, children }) {
  return (
    <div className="flex gap-3">
      <Icon className="h-4 w-4 text-primary shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="text-sm text-foreground leading-relaxed break-words">{children}</div>
      </div>
    </div>
  );
}

export default function IntegrationsAdmin() {
  const { isAdmin } = useAuth();

  if (!isAdmin) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center" data-testid="integrations-admin-denied">
        <Lock className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-2xl font-bold tracking-tight mb-2">Admins only</h1>
        <p className="text-sm text-muted-foreground">This page is restricted to administrators.</p>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 max-w-4xl mx-auto" data-testid="integrations-admin-page">
      <div className="mb-6">
        <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">System · Admin</span>
        <h1 className="text-3xl font-bold tracking-tight mt-1">Integrations</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Every third-party service Arqitek connects to. Each panel is collapsed by default — expand one to see what it
          does, how it authenticates, and where it's used in the app.
        </p>
      </div>

      <Accordion type="multiple" className="space-y-3" data-testid="integrations-accordion">
        {INTEGRATIONS.map((it) => {
          const Icon = it.icon;
          return (
            <AccordionItem
              key={it.slug}
              value={it.slug}
              className="border border-border rounded-xl bg-card px-4 data-[state=open]:border-primary/40 transition-colors"
              data-testid={`integration-item-${it.slug}`}
            >
              <AccordionTrigger className="hover:no-underline py-4" data-testid={`integration-trigger-${it.slug}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0 pr-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <Icon className="h-4.5 w-4.5 text-primary" />
                  </div>
                  <div className="min-w-0 text-left">
                    <div className="text-sm font-semibold truncate">{it.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{it.category}</div>
                  </div>
                  <span
                    className={`ml-auto shrink-0 text-[10px] font-medium px-2 py-0.5 rounded-full border ${STATUS_TONE[it.status] || ""}`}
                    data-testid={`integration-status-${it.slug}`}
                  >
                    {it.status}
                  </span>
                </div>
              </AccordionTrigger>
              <AccordionContent data-testid={`integration-content-${it.slug}`}>
                <div className="grid sm:grid-cols-2 gap-4 pb-4 pt-1">
                  <DetailRow icon={Wrench} label="What it does">{it.purpose}</DetailRow>
                  <DetailRow icon={KeyRound} label="Authentication">{it.auth}</DetailRow>
                  <DetailRow icon={Database} label="Data flow">{it.dataType}</DetailRow>
                  <DetailRow icon={Layers} label="Where it's used">{it.whereUsed}</DetailRow>
                  <div className="sm:col-span-2 flex flex-wrap items-center gap-4 pt-1 text-[12px] text-muted-foreground">
                    <span>Provider: <span className="text-foreground">{it.provider}</span></span>
                    {it.docs && (
                      <a
                        href={it.docs} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-primary hover:underline"
                        data-testid={`integration-docs-${it.slug}`}
                      >
                        Documentation <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
